import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent, type ReactNode } from 'react';

import { ApiError } from '@/shared/api/client';
import {
  brokers,
  type BankAccount,
  type Broker,
  type FinancialContact,
} from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import {
  formatCivilDate,
  formatDocument,
  formatPhone,
  formatRate,
  formatUf,
} from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/ui/Card';
import { DetailTabs } from '@/shared/ui/DetailLayout';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Field } from '@/shared/ui/Field';
import { PageHeader } from '@/shared/ui/PageHeader';
import { Spinner } from '@/shared/ui/Spinner';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import { useToast } from '@/shared/ui/useToast';

import { DocumentsPanel } from '../documents/DocumentsPanel';
import { fieldErrorOf } from '../shared/fieldError';
import { UsersPage } from '../users/UsersPage';

import { BrokerPartnersSection } from './BrokerPartnersSection';
import { permissaoDadosBancarios } from './contaBancaria';
import { FinancialDataFields } from './FinancialDataFields';
import { rotuloDaSituacaoDoCnpj } from './rotulos';

type Aba = 'gerais' | 'financeiros' | 'socios' | 'usuarios' | 'documentos';

/**
 * Dados da corretora, no portal da corretora.
 *
 * As abas sao as do OnPoint, na ordem dele: Dados Gerais, Dados Financeiros, Socios
 * / Administradores PEP, Dados Usuarios e Documentos. A corretora edita a razao
 * social, a inscricao municipal e os dados financeiros, e ve o resto do cadastro sem
 * poder muda-lo. O servidor repete a regra: o que a corretora nao decide fica como
 * esta gravado, qualquer que seja o valor enviado.
 *
 * A conta bancaria tem alcada propria (corretores.dados_bancarios.update), que o
 * administrador da corretora so tem se a administracao conceder. Sem ela, a conta
 * aparece travada.
 *
 * A corretora vem de GET /api/v1/brokers/current (Broker/GetCurrentBroker do OnPoint),
 * que so responde para quem tem escopo de corretora.
 */
export function BrokerSelfPage() {
  const { can, session } = useAuth();
  const daCorretora = session?.broker_scoped === true;
  const podeVer = can('corretores.corretor.view');

  // Sem escopo, a seguradora nao "e" uma corretora: a tela nem pergunta. A seguradora
  // cuida das corretoras em Corretores.
  const consulta = useQuery({
    queryKey: ['brokers', 'propria'],
    queryFn: ({ signal }) => brokers.current(signal),
    enabled: podeVer && daCorretora,
    retry: false,
  });

  const semCorretora =
    consulta.error instanceof ApiError && consulta.error.code === 'RESOURCE_NOT_FOUND';

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Dados da corretora"
        description="Cadastro da sua corretora junto a seguradora."
      />

      {!daCorretora ? (
        <Alert tone="info" title="Tela do portal da corretora">
          Esta tela mostra o cadastro da corretora de quem acessa pelo portal. As
          corretoras da empresa ficam em Corretores.
        </Alert>
      ) : !podeVer ? (
        <Alert tone="info" title="Indisponivel para o seu acesso">
          A consulta dos dados da corretora e restrita. Fale com a seguradora.
        </Alert>
      ) : consulta.isPending ? (
        <div className="flex justify-center py-16">
          <Spinner size={24} label="Carregando" className="text-brand-500" />
        </div>
      ) : semCorretora ? (
        <Card>
          <EmptyState
            icon="bank"
            title="Nenhuma corretora ligada ao seu acesso"
            description="Seu acesso ainda nao foi ligado a uma corretora. Fale com a seguradora."
          />
        </Card>
      ) : consulta.isError ? (
        <Alert tone="danger" title="Nao foi possivel carregar a corretora">
          {consulta.error.message}
        </Alert>
      ) : (
        <AbasDaCorretora corretora={consulta.data} />
      )}
    </div>
  );
}

function AbasDaCorretora({ corretora }: { corretora: Broker }) {
  const { can } = useAuth();
  const [aba, setAba] = useState<Aba>('gerais');

  const abas: { key: Aba; title: string }[] = [
    { key: 'gerais', title: 'Dados Gerais' },
    { key: 'financeiros', title: 'Dados Financeiros' },
    { key: 'socios', title: 'Socios / Administradores PEP' },
    ...(can('usuarios.usuario.view')
      ? [{ key: 'usuarios' as const, title: 'Dados Usuarios' }]
      : []),
    ...(can('documentos.corretora.view')
      ? [{ key: 'documentos' as const, title: 'Documentos' }]
      : []),
  ];

  return (
    <div className="flex flex-col gap-4">
      {corretora.has_pending_documents ? (
        <Alert tone="warning" title="Atencao!">
          A seguradora pediu {corretora.pending_documents_count ?? 0} documento(s)
          obrigatorio(s) ainda pendente(s). Envie o arquivo na aba Documentos.
        </Alert>
      ) : null}
      <DetailTabs
        tabs={abas}
        active={aba}
        onChange={setAba}
        label="Dados da corretora"
      />
      {/* A chave refaz o formulario quando a corretora gravada muda, para que ele
          nunca mostre um valor que o servidor ja substituiu. */}
      {aba === 'gerais' ? (
        <DadosGerais key={corretora.updated_at} corretora={corretora} />
      ) : null}
      {aba === 'financeiros' ? (
        <DadosFinanceiros key={corretora.updated_at} corretora={corretora} />
      ) : null}
      {aba === 'socios' ? (
        <BrokerPartnersSection corretoraId={corretora.id} podeEditar={false} />
      ) : null}
      {aba === 'usuarios' ? <UsersPage /> : null}
      {aba === 'documentos' ? (
        <DocumentsPanel
          ownerType="corretor"
          ownerId={corretora.id}
          title="Documentos"
          description="Documentos que a seguradora pediu a corretora. Envie o arquivo de cada um."
          allowUpload={false}
          allowRequire={false}
          showDeleted={false}
          emptyTitle="Nenhum documento pedido"
          emptyDescription="Quando a seguradora pedir um documento, ele aparece aqui."
        />
      ) : null}
    </div>
  );
}

function useSalvarCorretora(corretora: Broker, mensagem: string) {
  const toast = useToast();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (entrada: Parameters<typeof brokers.update>[1]) =>
      brokers.update(corretora.id, entrada),
    onSuccess: () => {
      toast.success(mensagem);
      void queryClient.invalidateQueries({ queryKey: ['brokers'] });
    },
  });
}

function DadosGerais({ corretora }: { corretora: Broker }) {
  const { can } = useAuth();
  const podeEditar = can('corretores.corretor.update');
  const [razaoSocial, setRazaoSocial] = useState(corretora.legal_name);
  const [inscricao, setInscricao] = useState(corretora.municipal_registration);
  const salvar = useSalvarCorretora(corretora, 'Dados gerais atualizados.');
  const erro = salvar.error instanceof ApiError ? salvar.error : null;

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    salvar.mutate({ legal_name: razaoSocial, municipal_registration: inscricao });
  }

  const endereco = corretora.address;

  return (
    <form onSubmit={enviar} className="flex flex-col gap-4">
      {salvar.error ? (
        <Alert tone="danger" title="Nao foi possivel salvar">
          {salvar.error.message}
        </Alert>
      ) : null}
      {!podeEditar ? (
        <Alert tone="info" title="Somente leitura">
          Estes dados sao editados pelo administrador da corretora.
        </Alert>
      ) : null}
      <Card>
        <CardHeader
          title="Dados gerais"
          description="O que nao for campo e mantido pela seguradora. Para corrigir, fale com ela."
        />
        <CardBody className="flex flex-col gap-5">
          <fieldset
            disabled={!podeEditar || salvar.isPending}
            className="grid gap-4 sm:grid-cols-2"
          >
            <Field
              label="Razao social"
              required
              value={razaoSocial}
              error={fieldErrorOf(erro, 'legal_name')}
              onChange={(evento) => setRazaoSocial(evento.target.value)}
            />
            <Field
              label="Inscricao municipal"
              maxLength={30}
              value={inscricao}
              error={fieldErrorOf(erro, 'municipal_registration')}
              onChange={(evento) => setInscricao(evento.target.value)}
            />
          </fieldset>

          <dl className="grid gap-3 sm:grid-cols-3">
            <Dado rotulo="CNPJ">{formatDocument(corretora.document)}</Dado>
            <Dado rotulo="Comissao">{formatRate(corretora.commission_bps)}</Dado>
            <Dado rotulo="Situacao do CNPJ">
              {rotuloDaSituacaoDoCnpj(corretora.cnpj_status) || <Ausente />}
            </Dado>
            <Dado rotulo="Codigo CNAE">
              {corretora.cnae_code ? (
                `${corretora.cnae_code} ${corretora.cnae_description ?? ''}`.trim()
              ) : (
                <Ausente />
              )}
            </Dado>
            <Dado rotulo="Ramo">{corretora.cnae_branch || <Ausente />}</Dado>
            <Dado rotulo="Data do registro">
              {formatCivilDate(corretora.susep_registered_on) || <Ausente />}
            </Dado>
            <Dado rotulo="Data do recadastro">
              {formatCivilDate(corretora.reregistered_on) || 'Sem registro'}
            </Dado>
            <Dado rotulo="Data expiracao do cadastro">
              {formatCivilDate(corretora.registration_expires_on) || <Ausente />}
            </Dado>
            <Dado rotulo="Situacao SUSEP">
              <StatusBadge kind="situacaoExterna" value={corretora.external_status} />
            </Dado>
            <Dado rotulo="Codigo SUSEP">{corretora.susep_code || <Ausente />}</Dado>
            <Dado rotulo="Telefone">
              {corretora.phone ? formatPhone(corretora.phone) : <Ausente />}
            </Dado>
            <Dado rotulo="Situacao na seguradora">
              <StatusBadge kind="corretor" value={corretora.stage} />
            </Dado>
            <Dado rotulo="Canal">{corretora.channel?.name || <Ausente />}</Dado>
            <Dado rotulo="Nome fantasia">{corretora.trade_name || <Ausente />}</Dado>
            <Dado rotulo="E-mail">{corretora.email || <Ausente />}</Dado>
          </dl>

          <dl className="grid gap-3 sm:grid-cols-3">
            <Dado rotulo="CEP">{endereco.zip_code || <Ausente />}</Dado>
            <Dado rotulo="Endereco">
              {[endereco.street, endereco.number].filter(Boolean).join(', ') || (
                <Ausente />
              )}
            </Dado>
            <Dado rotulo="Complemento">{endereco.complement || <Ausente />}</Dado>
            <Dado rotulo="Bairro">{endereco.district || <Ausente />}</Dado>
            <Dado rotulo="UF">
              {endereco.state ? formatUf(endereco.state) : <Ausente />}
            </Dado>
            <Dado rotulo="Cidade">{endereco.city || <Ausente />}</Dado>
          </dl>
        </CardBody>
      </Card>
      {podeEditar ? (
        <div className="flex justify-end">
          <Button type="submit" loading={salvar.isPending}>
            Salvar
          </Button>
        </div>
      ) : null}
    </form>
  );
}

function DadosFinanceiros({ corretora }: { corretora: Broker }) {
  const { can } = useAuth();
  const podeEditar = can('corretores.corretor.update');
  // A conta bancaria tem alcada propria, que o administrador da corretora nao tem
  // por padrao: quem a concede e a administracao.
  const podeDadosBancarios = podeEditar && can(permissaoDadosBancarios);
  const [conta, setConta] = useState<BankAccount>(corretora.bank_account);
  const [responsavel, setResponsavel] = useState<FinancialContact>(
    corretora.financial_contact,
  );
  const salvar = useSalvarCorretora(corretora, 'Dados financeiros atualizados.');
  const erro = salvar.error instanceof ApiError ? salvar.error : null;

  // A tela envia so o que a corretora decide. Sem a alcada da conta, ela nem vai no
  // pedido.
  function enviar(evento: FormEvent) {
    evento.preventDefault();
    salvar.mutate({
      ...(podeDadosBancarios ? { bank_account: conta } : {}),
      financial_contact: responsavel,
    });
  }

  return (
    <form onSubmit={enviar} className="flex flex-col gap-4">
      {salvar.error ? (
        <Alert tone="danger" title="Nao foi possivel salvar">
          {salvar.error.message}
        </Alert>
      ) : null}
      {!podeEditar ? (
        <Alert tone="info" title="Somente leitura">
          Estes dados sao editados pelo administrador da corretora.
        </Alert>
      ) : null}
      <Card>
        <CardHeader
          title="Dados financeiros"
          description="A conta que recebe os repasses de comissao e quem responde pelo financeiro."
        />
        <CardBody className="flex flex-col gap-4">
          {podeDadosBancarios ? (
            <Alert tone="warning" title="Confira antes de salvar">
              A conta bancaria e o destino dos repasses. Toda alteracao fica registrada
              com o valor anterior, o novo e quem alterou.
            </Alert>
          ) : null}
          <fieldset disabled={!podeEditar || salvar.isPending}>
            <FinancialDataFields
              conta={conta}
              responsavel={responsavel}
              erro={erro}
              contaSomenteLeitura={!podeDadosBancarios}
              onContaChange={setConta}
              onResponsavelChange={setResponsavel}
            />
          </fieldset>
        </CardBody>
      </Card>
      {podeEditar ? (
        <div className="flex justify-end">
          <Button type="submit" loading={salvar.isPending}>
            Salvar dados financeiros
          </Button>
        </div>
      ) : null}
    </form>
  );
}

function Dado({ rotulo, children }: { rotulo: string; children: ReactNode }) {
  return (
    <div>
      <dt className="text-2xs font-semibold tracking-wide text-ink-500 uppercase">
        {rotulo}
      </dt>
      <dd className="mt-0.5 text-xs text-ink-900">{children}</dd>
    </div>
  );
}

function Ausente() {
  return <span className="text-ink-400">nao informado</span>;
}
